import { useState, useEffect } from 'react';
import { Layout, Button, Tooltip, Drawer } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme, THEMES } from '../../contexts/ThemeContext';
import './Landing.css';
import UseCases from './UseCases';
import HeroSection from './HeroSection';
import FeaturesSection from './FeaturesSection';
import TestimonialsSection from './TestimonialsSection';
import PlatformSection from './PlatformSection';
import PricingSection from './PricingSection';
import AISection from './AISection';
import Icon from '../common/Icon';

const { Header, Content, Footer } = Layout;

export default function LandingPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { theme, setTheme, isDark } = useTheme();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    // Detect mobile viewport
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
        setMobileMenuOpen(false);
    };

    const toggleTheme = () => {
        setTheme(isDark ? THEMES.LIGHT : THEMES.DARK);
    };

    const navItems = [
        { label: 'Use Cases', id: 'use-cases' },
        { label: 'Features', id: 'features' },
        { label: 'AI', id: 'ai' },
        { label: 'Pricing', id: 'pricing' },
    ];

    return (
        <Layout className="landing-layout">
            <Header className="landing-header">
                <div className="landing-logo">
                    <span className="logo-icon">📝</span>
                    <span className="logo-text">Page Notes</span>
                </div>
                
                {/* Desktop Navigation */}
                <div className="landing-nav desktop-nav">
                    {navItems.map(item => (
                        <Button
                            key={item.id}
                            type="text"
                            className="nav-link"
                            onClick={() => scrollToSection(item.id)}
                        >
                            {item.label}
                        </Button>
                    ))}
                    <Tooltip title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
                        <Button
                            type="text"
                            className="theme-toggle-btn"
                            onClick={toggleTheme}
                            icon={<Icon name={isDark ? 'Sun' : 'Moon'} size="sm" />}
                        />
                    </Tooltip>
                    <Button type="primary" onClick={() => scrollToSection('download')} style={{ marginLeft: 8 }}>
                        Get the App
                    </Button>
                    {user ? (
                        <Button onClick={() => navigate('/notes')}>
                            Go to Notes
                        </Button>
                    ) : (
                        <Button type="default" ghost={isDark} className="signin-btn" onClick={() => navigate('/login')}>
                            Sign In
                        </Button>
                    )}
                </div>

                {/* Mobile Navigation */}
                <div className="landing-nav mobile-nav">
                    <Tooltip title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
                        <Button
                            type="text"
                            className="theme-toggle-btn"
                            onClick={toggleTheme}
                            icon={<Icon name={isDark ? 'Sun' : 'Moon'} size="sm" />}
                        />
                    </Tooltip>
                    <Button
                        type="text"
                        className="mobile-menu-btn"
                        onClick={() => setMobileMenuOpen(true)}
                        icon={<Icon name="Menu" size="md" />}
                    />
                </div>

                {/* Mobile Drawer */}
                <Drawer
                    title={
                        <div className="mobile-drawer-header">
                            <span className="logo-icon">📝</span>
                            <span>Page Notes</span>
                        </div>
                    }
                    placement="right"
                    onClose={() => setMobileMenuOpen(false)}
                    open={mobileMenuOpen}
                    width={280}
                    className="mobile-menu-drawer"
                >
                    <div className="mobile-menu-content">
                        {navItems.map(item => (
                            <Button
                                key={item.id}
                                type="text"
                                block
                                className="mobile-nav-link"
                                onClick={() => scrollToSection(item.id)}
                            >
                                {item.label}
                            </Button>
                        ))}
                        <div className="mobile-menu-divider" />
                        <Button
                            type="primary"
                            block
                            onClick={() => {
                                scrollToSection('download');
                                setMobileMenuOpen(false);
                            }}
                        >
                            Get the App
                        </Button>
                        {user ? (
                            <Button
                                block
                                onClick={() => {
                                    navigate('/notes');
                                    setMobileMenuOpen(false);
                                }}
                            >
                                Go to Notes
                            </Button>
                        ) : (
                            <Button
                                type="default"
                                block
                                onClick={() => {
                                    navigate('/login');
                                    setMobileMenuOpen(false);
                                }}
                            >
                                Sign In
                            </Button>
                        )}
                    </div>
                </Drawer>
            </Header>
            <Content className="landing-content">
                <HeroSection />
                <UseCases />
                <FeaturesSection />
                <AISection />
                <TestimonialsSection />
                <PlatformSection />
                <PricingSection />
            </Content>
            <Footer className="landing-footer">
                Page Notes ©{new Date().getFullYear()} - The Context-Aware Web Notepad
            </Footer>
        </Layout>
    );
}
